import { useEffect, useRef, useState, type ComponentType } from 'react'

import {
  getFlagDebugEvents,
  isFlagDebugEnabled,
  publishFlagDebugEvent,
  setFlagDebugEnabled,
} from './debugBus.js'
import { useFlagsSnapshot } from './FlagsProvider.js'

/**
 * Always-mounted gate for the flag debug overlay.
 *
 * This is the only piece of the debug surface a normal visitor downloads, so it
 * stays deliberately small: install the console API, publish the SSR snapshot to
 * the bus, and — only when the switch is on — dynamically import the overlay.
 * The overlay's markup and formatting stay out of the main bundle, which is what
 * makes it acceptable to ship this to production.
 *
 * TURN IT ON (any environment) — set the key, then reload:
 *
 *     localStorage.setItem('bm_flag_debug', '1')
 *
 * Once on, a console API is available:
 *
 *     __bmFlags.disable()   // reloads with the overlay off
 *     __bmFlags.events()    // the raw event log
 *
 * `__bmFlags` is installed ONLY when the overlay is already enabled, and that is a
 * constraint rather than a convenience. Communities inject arbitrary third-party
 * JS into this same `window` (`network.customCodes`), so an always-present
 * `enable()` is a reload primitive on every page: a snippet calling it on load
 * traps every visitor to that community in a reload loop with no product-visible
 * off switch. The `localStorage` bootstrap costs an engineer one line and keeps
 * the primitive off the page; `setFlagDebugEnabled` no-opping on an unchanged
 * value is the other half of the same guarantee.
 *
 * HYDRATION INVARIANT — this must render `null` on its FIRST render.
 * `SSRApp` renders `FlagsProvider` WITHOUT this component, while `ClientApp`
 * includes it and the client `hydrateRoot`s. The trees therefore only agree
 * because this contributes no DOM until an effect has run and the dynamic import
 * has resolved, both of which happen after hydration. Rendering anything
 * synchronously here would produce a hydration mismatch on every page in the app.
 * `FlagDebugMount.test.tsx` pins this.
 */
export const FlagDebugMount = () => {
  const snapshot = useFlagsSnapshot()
  const [Overlay, setOverlay] = useState<ComponentType | null>(null)

  useEffect(() => {
    if (!isFlagDebugEnabled()) {
      // Not enabled: nothing is installed and nothing is loaded, so a normal
      // session carries no debug surface at all — not even the global. See the
      // docblock for the localStorage bootstrap that turns it on.
      return () => undefined
    }

    ;(globalThis as Record<string, unknown>).__bmFlags = {
      disable: () => setFlagDebugEnabled(false),
      events: () => [...getFlagDebugEvents()],
    }

    let active = true
    import('./FlagDebugOverlay.js')
      .then(module => {
        if (active) {
          setOverlay(() => module.FlagDebugOverlay)
        }
      })
      .catch(() => {
        // A debug surface that fails to load must stay silent, not surface an
        // unhandled rejection in a normal session.
      })
    return () => {
      active = false
    }
  }, [])

  // Publish once per snapshot so the log starts with what the page rendered
  // with, rather than only showing activity that happened after mount.
  //
  // Keyed on CONTENT, not object identity: a parent that re-renders with an
  // equal-but-new `flags` object would otherwise republish on every pass and
  // evict real history from the ring buffer. `injectedAppProps.flags` happens to
  // be stable today, so this is insurance against a future caller, not a fix for
  // a live bug.
  const snapshotRef = useRef(snapshot)
  snapshotRef.current = snapshot
  const snapshotKey = JSON.stringify(snapshot)
  useEffect(() => {
    publishFlagDebugEvent({
      kind: 'snapshot',
      at: Date.now(),
      flags: snapshotRef.current,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- content-keyed by design; see above
  }, [snapshotKey])

  return Overlay ? <Overlay /> : null
}
